import { pushEvent, pushRaw } from './index';
import * as EVENTS from './events';
import getWizardStaticParams from './wizardConfig';

// Page / session - "GTM Guidelines Wizard" convention (brand, countryCode,
// googleAccount, businessType come from per-deployment static config;
// languageCode/pageName/pageType/dealerName/dealerId/vehicleModel/vehicleId/
// vehicle are dynamic, gathered by the caller via useTrackingContext()).

// The real (non-virtual) page load - fires once per browser page load,
// regardless of how the user got here (dedicated link, model page, grade
// carousel). No 'event' key: GTM's own automatic Page View trigger picks
// these variables up from the dataLayer on container load.
export const trackPageView = (context = {}) => pushRaw({
    ...getWizardStaticParams(),
    ...context,
});

export const trackVirtualPageview = (pageName, pageType, context = {}) => pushEvent(EVENTS.VIRTUAL_PAGEVIEW, {
    ...getWizardStaticParams(),
    pageName,
    pageType,
    ...context,
});

// Configurator flow
export const trackConfiguratorStart = () => pushEvent(EVENTS.CONFIGURATOR_START, {
    step_name: 'start',
    step_number: 0,
});

export const trackConfiguratorStep = (stepName, stepNumber) => pushEvent(EVENTS.CONFIGURATOR_STEP, {
    step_name: stepName,
    step_number: stepNumber,
});

// Fires when the user closes the configurator via one of the 3 conversion
// paths (ask offer / book test drive / save PDF) - see StickyAside, the
// single place all 3 of those actions live.
export const trackConfiguratorComplete = (totalPrice, context = {}) => pushEvent(EVENTS.CONFIGURATOR_COMPLETE, {
    ...getWizardStaticParams(),
    configStepName: 'complete',
    configStepNumber: 'step-3',
    pageName: 'configure',
    pageType: 'configurator',
    totalPrice,
    ...context,
});

// Forms / leads
export const trackFormOpen = (formType) => pushEvent(EVENTS.FORM_OPEN, {
    form_type: formType,
});

export const trackFormStep = (formType, stepName, stepNumber) => pushEvent(EVENTS.FORM_STEP, {
    form_type: formType,
    step_name: stepName,
    step_number: stepNumber,
});

export const trackFormSubmit = (formType, params = {}) => pushEvent(EVENTS.FORM_SUBMIT, {
    form_type: formType,
    ...params,
});

export const trackBrochureDownload = (modelName) => pushEvent(EVENTS.BROCHURE_DOWNLOAD, {
    model_name: modelName,
});

// Declared for future use - no current UI action triggers these yet.
export const trackDealerSearch = (searchTerm) => pushEvent(EVENTS.DEALER_SEARCH, {
    search_term: searchTerm,
});

export const trackOfferView = (modelName) => pushEvent(EVENTS.OFFER_VIEW, {
    model_name: modelName,
});

export const trackFinanceInteraction = (interactionType) => pushEvent(EVENTS.FINANCE_INTERACTION, {
    interaction_type: interactionType,
});

// GA4 ecommerce
const ecommerceEvent = (event, items, extra = {}) => pushEvent(event, {
    ecommerce: {
        items: Array.isArray(items) ? items : [items],
        ...extra,
    },
});

export const trackViewItem = (item, extra) => ecommerceEvent(EVENTS.VIEW_ITEM, item, extra);
export const trackSelectItem = (item, extra) => ecommerceEvent(EVENTS.SELECT_ITEM, item, extra);
export const trackAddToCart = (item, extra) => ecommerceEvent(EVENTS.ADD_TO_CART, item, extra);
export const trackRemoveFromCart = (item, extra) => ecommerceEvent(EVENTS.REMOVE_FROM_CART, item, extra);
export const trackBeginCheckout = (items, extra) => ecommerceEvent(EVENTS.BEGIN_CHECKOUT, items, extra);
export const trackPurchase = (items, extra) => ecommerceEvent(EVENTS.PURCHASE, items, extra);
